// Open talk (the person's option, 2026-10-23: "static options vs open-ended with AI suggestions"): instead of a
// script's written lines and choices, the player types anything or picks one of a few suggested lines in the
// protagonist's voice, and a provider writes the person's replies. The provider is the AI's seam (the judge and
// social packages, G01 and G02, plug in here); `scriptedOpenTalk` is a deterministic one from content, for labs, tests
// and a game played offline.
//
// Determinism: a provider may answer at once or later (a promise). An answer that arrives later waits in an inbox and
// is applied at the feature's next step, so the sim only ever changes inside a step; every answer applied is kept in
// the talk's state (its history), so a save holds what was said. A request still out when the game saves is asked
// again after the load. Each request carries its own number, which the talk never reuses (not saved: a load asks again
// under a new one); an answer applies only to the request still out under its number, so an answer for a talk that has
// since closed, moved on, been started again, or been replaced by a load, is dropped.
import type { FactValue } from "@rpg/story";

/** Planted defects beside what they break: a game's open-talk checks must go red with them on. */
export const OPEN_TALK_FAULTS = {
  /** An answer is matched by the person, the turn and the kind only (as in 0.1.0): a late answer from an earlier
   *  talk with the same person, or from before a load, shows in the talk now open. */
  staleAnswerKept: false,
};

/** What a provider is told: who, the talk so far, the feature's flags, and the turn. */
export interface OpenTalkContext {
  npc: string;
  /** The person's name, as words. */
  name: string;
  /** Every line so far: `speaker` is the npc's id or "player". */
  history: readonly { speaker: string; text: string }[];
  flags: Readonly<Record<string, FactValue>>;
  turn: number;
  /** What the talk is about, when a scene or story beat asked for it ("gate": the basket left at the stall). */
  topic?: string;
}

/** A provider's reply: the person's lines, whether the talk ends after them, and flags to set. */
export interface OpenReply {
  lines: readonly string[];
  end?: boolean;
  set?: Readonly<Record<string, FactValue>>;
}

/** Writes an open talk: a few lines the player might say, and the person's replies (`said` "" asks for the greeting).
 *  Either may answer at once or with a promise; a throw or a rejection falls back to the person's script. */
export interface OpenTalkProvider {
  suggest(ctx: OpenTalkContext): readonly string[] | Promise<readonly string[]>;
  reply(ctx: OpenTalkContext, said: string): OpenReply | Promise<OpenReply>;
}

/** One person's lines for {@link scriptedOpenTalk}: a greeting, replies by the words the player used, a reply for
 *  anything else, and suggestions by turn (the last set repeats); `topics` replace any of them for a talk about a
 *  topic. */
export interface ScriptedPerson {
  greeting: readonly string[];
  rules: readonly { words: readonly string[]; reply: readonly string[]; end?: boolean; set?: Readonly<Record<string, FactValue>> }[];
  otherwise: readonly string[];
  suggestions: readonly (readonly string[])[];
  topics?: Readonly<Record<string, Partial<Omit<ScriptedPerson, "topics">>>>;
}

/** A deterministic provider from content: the first rule sharing a word with what was said answers, else `otherwise`.
 *  Not an AI: a stand-in with the same seam, for labs, tests and offline play. */
export function scriptedOpenTalk(people: Readonly<Record<string, ScriptedPerson>>): OpenTalkProvider {
  const person = (ctx: OpenTalkContext): ScriptedPerson => {
    const p = people[ctx.npc];
    if (!p) throw new Error(`conversation: no open-talk lines for "${ctx.npc}"`);
    const t = ctx.topic !== undefined ? p.topics?.[ctx.topic] : undefined;
    return t ? { ...p, ...t } : p;
  };
  return {
    suggest: (ctx) => {
      const s = person(ctx).suggestions;
      return s.length ? s[Math.min(ctx.turn - 1, s.length - 1)]! : [];
    },
    reply: (ctx, said) => {
      const p = person(ctx);
      if (!said) return { lines: p.greeting };
      const words = new Set(said.toLowerCase().match(/[\p{L}\p{N}']{3,}/gu) ?? []);
      const rule = p.rules.find((r) => r.words.some((w) => words.has(w.toLowerCase())));
      return rule ? { lines: rule.reply, ...(rule.end ? { end: true } : {}), ...(rule.set ? { set: rule.set } : {}) } : { lines: p.otherwise };
    },
  };
}

/** An open talk's state (saved with the feature). */
export interface OpenState {
  npc: string;
  turn: number;
  history: { speaker: string; text: string }[];
  /** Reply lines waiting to show, and the one showing. */
  queue: string[];
  line: string | null;
  /** The suggestions showing (null: none asked for yet this turn). */
  suggestions: string[] | null;
  /** The request out: what, and on which turn. */
  pending: { kind: "reply"; said: string; turn: number } | { kind: "suggest"; turn: number } | null;
  /** The talk ends once the queued lines are read. */
  ending: boolean;
  /** What it is about (a scene's or story beat's topic), if anything. */
  topic?: string;
  /** The script that plays in its place if the provider fails. */
  fallback?: { script: string; knot?: string };
}

/** A provider's answer waiting for the next step: `ask` is the number of the request it answers. */
type Answer = { ask: number; turn: number; npc: string; for: "reply" | "suggest" } & ({ kind: "reply"; reply: OpenReply } | { kind: "suggest"; lines: readonly string[] } | { kind: "error" });

/** The open talk's mechanics, driven by the feature. */
export class OpenTalk {
  state: OpenState | null = null;
  private inbox: Answer[] = [];
  /** The last request's number (never reused by this talk, never saved), and the one still out (0: none). */
  private asked = 0;
  private out = 0;
  constructor(
    private readonly provider: OpenTalkProvider,
    private readonly hooks: {
      name(npc: string): string;
      flags(): Readonly<Record<string, FactValue>>;
      setFlags(set: Readonly<Record<string, FactValue>>): void;
      /** A line shows (the feature emits it and runs the clock). */
      line(npc: string, text: string, turn: number, index: number): void;
      /** The provider failed: the feature plays the talk's fallback script, or closes it. */
      failed(npc: string, fallback: OpenState["fallback"]): void;
      closed(npc: string): void;
    },
  ) {}
  private ctx(): OpenTalkContext {
    const s = this.state!;
    return { npc: s.npc, name: this.hooks.name(s.npc), history: s.history.map((h) => ({ ...h })), flags: { ...this.hooks.flags() }, turn: s.turn, ...(s.topic !== undefined ? { topic: s.topic } : {}) };
  }
  private ask(p: NonNullable<OpenState["pending"]>): void {
    const s = this.state!;
    s.pending = p;
    this.out = ++this.asked;
    const tag = { ask: this.out, turn: p.turn, npc: s.npc, for: p.kind };
    const take = (a: Answer): void => {
      this.inbox.push(a);
    };
    try {
      const out = p.kind === "reply" ? this.provider.reply(this.ctx(), p.said) : this.provider.suggest(this.ctx());
      if (out instanceof Promise)
        out.then(
          (v) => take(p.kind === "reply" ? { ...tag, kind: "reply", reply: v as OpenReply } : { ...tag, kind: "suggest", lines: v as readonly string[] }),
          () => take({ ...tag, kind: "error" }),
        );
      else this.apply(p.kind === "reply" ? { ...tag, kind: "reply", reply: out as OpenReply } : { ...tag, kind: "suggest", lines: out as readonly string[] });
    } catch {
      this.apply({ ...tag, kind: "error" });
    }
  }
  private apply(a: Answer): void {
    const s = this.state;
    // an answer to a request no longer out (the talk closed, moved on, started again, was loaded over, or the player
    // spoke first) is dropped
    if (!s || s.npc !== a.npc || !s.pending || s.pending.turn !== a.turn || s.pending.kind !== a.for) return;
    if (a.ask !== this.out && !OPEN_TALK_FAULTS.staleAnswerKept) return;
    s.pending = null;
    this.out = 0;
    if (a.kind === "error") {
      this.state = null;
      this.hooks.failed(s.npc, s.fallback);
      return;
    }
    if (a.kind === "suggest") s.suggestions = [...a.lines];
    else {
      if (a.reply.set) this.hooks.setFlags(a.reply.set);
      s.queue.push(...a.reply.lines);
      s.ending = a.reply.end === true;
      this.next();
    }
  }
  private next(): void {
    const s = this.state!;
    const t = s.queue.shift();
    if (t === undefined) {
      s.line = null;
      if (s.ending) return this.close();
      s.turn++;
      s.suggestions = null;
      return this.ask({ kind: "suggest", turn: s.turn });
    }
    s.line = t;
    s.history.push({ speaker: s.npc, text: t });
    this.hooks.line(s.npc, t, s.turn, s.history.length);
  }
  /** Begin a talk with `npc` (about `topic`, with a `fallback` script): ask for the greeting. */
  start(npc: string, o: { topic?: string; fallback?: { script: string; knot?: string } } = {}): void {
    this.inbox = [];
    this.out = 0;
    this.state = { npc, turn: 0, history: [], queue: [], line: null, suggestions: null, pending: null, ending: false, ...(o.topic !== undefined ? { topic: o.topic } : {}), ...(o.fallback ? { fallback: o.fallback } : {}) };
    this.ask({ kind: "reply", said: "", turn: 0 });
  }
  /** Past the line showing: the next queued line, else the player's turn (suggestions asked for), or the end. */
  advance(): void {
    const s = this.state;
    if (!s || s.pending || s.line === null) return;
    this.next();
  }
  /** The player says something (typed, or a suggestion): the person's reply is asked for. */
  say(text: string): void {
    const s = this.state;
    const said = text.trim();
    if (!s || !said || s.line !== null || s.pending?.kind === "reply") return;
    s.history.push({ speaker: "player", text: said });
    s.suggestions = null;
    this.ask({ kind: "reply", said, turn: s.turn });
  }
  /** Say the suggestion at `n`. */
  choose(n: number): void {
    const t = this.state?.suggestions?.[n];
    if (t !== undefined) this.say(t);
  }
  close(): void {
    const s = this.state;
    this.state = null;
    this.inbox = [];
    this.out = 0;
    if (s) this.hooks.closed(s.npc);
  }
  /** Apply the answers that arrived since the last step (the feature's system calls it each step). */
  step(): void {
    const ready = this.inbox;
    this.inbox = [];
    for (const a of ready) this.apply(a);
  }
  /** After a load: ask again for whatever was out when the game saved. */
  resume(state: OpenState | null, pendingLost = false): void {
    this.inbox = [];
    this.out = 0;
    this.state = state;
    if (state?.pending && !pendingLost) this.ask(state.pending);
    else if (state) state.pending = null;
  }
}
